/* eslint-disable @scandipwa/scandipwa-guidelines/jsx-no-props-destruction */
/* eslint-disable max-lines */
/**
 * Copyright © Scandiweb, Inc. All rights reserved.
 *
 * @category    Puma
 * @package     Puma_MenuFilters
 * @author      Deniss Strombergs <info@scandiweb.com>
 */
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { STORE_CODE_EN, STORE_CODE_KSA_AR, STORE_CODE_UAE } from 'Component/Router/Router.config';
import { CategoryTreeType } from 'Type/Category.type';
import MenuHelper, { fetchMenu } from 'Util/Menu';
import DataContainer from 'Util/Request/DataContainer';

import MenuFiltersComponent from './MenuFilters.component';

/** @namespace Scandipwa/Component/MenuFilters/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    category: state.CategoryReducer.category,
    filterTitle: state.ConfigReducer.filterTitle
});

/** @namespace Scandipwa/Component/MenuFilters/Container/mapDispatchToProps */
export const mapDispatchToProps = () => ({});

/** @namespace Scandipwa/Component/MenuFilters/Container */
export class MenuFiltersContainer extends DataContainer {
    static propTypes = {
        category: CategoryTreeType.isRequired,
        menuFiltersHeight: PropTypes.number,
        setMenuFiltersHeight: PropTypes.func,
        areComponentsLoading: PropTypes.bool.isRequired,
        filterTitle: PropTypes.string,
        isSearchPage: PropTypes.bool.isRequired
    };

    static defaultProps = {
        menuFiltersHeight: undefined,
        setMenuFiltersHeight: () => null,
        isSearchPage: false,
        filterTitle: ''
    };

    state = {
        menu: {}
    };

    componentDidMount() {
        this._getMenu();
    }

    getUrlParts(url) {
        return (typeof url === 'object' ? url.pathname || '/' : url).substr(1).replace('.html', '').split('/');
    }

    _getMenu() {
        fetchMenu((menu) => this.setState({ menu: MenuHelper.reduce(menu) }));
    }

    getUrl(url) {
        return typeof url === 'object' ? url.pathname || '/' : url;
    }

    onClick(url, categoryId) {
        const { requestCategoryFromFilters } = this.props;

        requestCategoryFromFilters(parseInt(categoryId, 10), url);
    }

    containerFunctions = {
        onClick: this.onClick.bind(this),
        getRenderingData: this.getRenderingData.bind(this)
    };

    isSubCategoryActive(children) {
        return Boolean(children.find((a) => a.isActive));
    }

    getSubsubcategories(children, url) {
        return children.map(
            (a) => ({
                title: a.title,
                // eslint-disable-next-line no-nested-ternary
                id: a.id ? a.id : (a?.url?.state?.category ? a.url.state.category : null),
                url: this.getUrl(a.url),
                isActive: this.getUrl(a.url) === url
            })
        );
    }

    getOtherSubcategories(currentCategory, url) {
        return Object.values(currentCategory.children).filter(
            (a) => !a.item_class.includes('promotional') && !a.item_class.includes('hidden')
        ).map(
            (a) => ({
                title: a.title,
                children: this.getSubsubcategories(Object.values(a.children), url),
                isActive: this.getUrlParts(url).pop() === this.getUrlParts(a.url).pop()
                    || this.isSubCategoryActive(this.getSubsubcategories(Object.values(a.children), url))
            })
        );
    }

    getCollectionsSubcategory(currentCategory, url) {
        const subsubcategories = this.getSubsubcategories(
            [
                ...Object.values(currentCategory.children).filter((a) => a.item_class.includes('promotional')),
                ...Object.values(currentCategory.children).filter((a) => a.item_class.includes('hidden'))
                    .map((a) => this.getSubsubcategories(Object.values(a.children, url))).flat()
            ],
            url
        );

        return {
            title: 'Collections',
            children: subsubcategories,
            isActive: this.isSubCategoryActive(subsubcategories, url)
        };
    }

    searchHiddenCategory(children, id) {
        return Object.values(children).find((item) => {
            const { url: { state: { category } = {} } = {} } = item;

            if (category === id) {
                return item;
            }

            return false;
        });
    }

    getCurrentCategoryItem(data, isMain = false) {
        const { children = [] } = data;
        const { category: { id } } = this.props;

        return Object.values(isMain ? data : children).find((item) => {
            const { url: { state: { category } = {} } = {}, item_class = '', children = [] } = item;

            if (item_class.includes('hidden')) {
                const childOfHiddenCategory = this.searchHiddenCategory(children, id);

                if (childOfHiddenCategory) {
                    return childOfHiddenCategory;
                }
            }

            if (category === id) {
                return item;
            }

            return false;
        });
    }

    getCurrentCategoryItemWithReduce(data, isDepest = false) {
        if (!data) {
            return false;
        }

        return data.reduce((acc, child) => {
            const result = isDepest
                ? this.getCurrentCategoryItemWithReduce(Object.values(child.children))
                : this.getCurrentCategoryItem(child);

            if (result) {
                acc.push(child);
            }

            return acc;
        }, [])[0];
    }

    getStoreCode() {
        // eslint-disable-next-line fp/no-let
        let storeCode = '';
        const firstPathElement = window.location.pathname.split('/')[1];
        switch (firstPathElement) {
        case STORE_CODE_EN:
            storeCode = `/${ STORE_CODE_EN }`;
            break;
        case STORE_CODE_UAE:
            storeCode = `/${ STORE_CODE_UAE }`;
            break;
        case STORE_CODE_KSA_AR:
            storeCode = `/${ STORE_CODE_KSA_AR }`;
            break;
        default:
            break;
        }

        return storeCode;
    }

    findCurrentCategory(childArray, url) {
        const storeCode = this.getStoreCode();
        const categoryLevel = url.replace(storeCode, '').substr(1).split('/').length.toString();

        switch (categoryLevel) {
        case '1':
            return this.getCurrentCategoryItem(childArray, true);
        case '2':
            const categoryItems = this.getCurrentCategoryItemWithReduce(childArray);

            // handle categories like new arrivals being at 2 level where it should be 3 level
            if (categoryItems) {
                if (Object.values(Object.values(categoryItems.children)[0].children).length === 0) {
                    return this.getCurrentCategoryItemWithReduce(childArray, true);
                }
            } else {
                return this.getCurrentCategoryItemWithReduce(childArray, true);
            }

            return categoryItems;
        case '3':
            return this.getCurrentCategoryItemWithReduce(childArray, true);
        default:
            return false;
        }
    }

    getRenderingData() {
        const { menu } = this.state;
        const { category } = this.props;
        const { children } = Object.values(menu)[0] || {};

        if (!children) {
            return null;
        }

        const childArray = Object.values(children);
        const { url } = category;

        if (!childArray || !url) {
            return null;
        }

        // workaround for search page filters
        // we will return harmless data that will comply with the expected format
        if (url === '/search/') {
            return [true];
        }

        const currentCategory = this.findCurrentCategory(childArray, url);

        if (!currentCategory) {
            return [];
        }

        return [
            ...this.getOtherSubcategories(currentCategory, url),
            this.getCollectionsSubcategory(currentCategory, url)
        ];
    }

    render() {
        return (
            <MenuFiltersComponent
              { ...this.props }
              { ...this.state }
              { ...this.containerFunctions }
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MenuFiltersContainer);
